"use client";

import {
  type MouseEvent as ReactMouseEvent,
  type RefObject,
  useEffect,
  useRef,
} from "react";

import { cx } from "@tripcart/traveler-ui/cx";

import { SITE_CONTAINER } from "@/components/container";
import { useTranslator } from "@/lib/translation-context";

import { ShareButton } from "./_hero/share-button.client";
import { landOnSection } from "./_scroll-landing";
import { markerOffset, useActiveSection } from "./_tour-nav.client";
import type { TourTab } from "./_section-order";
import styles from "./_tour-tabs-bar.module.css";

/** The bar machinery every per-tab click handler needs, grouped so
 *  `scrollToSection` takes one object instead of three positional refs/lists
 *  that all come from the same `TourTabsBar` instance anyway. */
interface TabBarContext {
  navRef: RefObject<HTMLElement | null>;
  pendingScroll: RefObject<(() => void) | null>;
  tabs: readonly TourTab[];
}

/**
 * Smooth-scroll a section to the marker on click.
 *
 * The target is computed here rather than left to `scrollIntoView` + CSS. That
 * route silently combined `scroll-mt-32` with the document's
 * `scroll-padding-top`, landing every section 72px below where the scrollspy
 * was watching. Computing from the same `markerOffset` the spy uses removes
 * the possibility of that disagreement.
 *
 * The native `<a href="#slug">` stays as the no-JS / SSR fallback, and
 * modified clicks (open-in-new-tab etc.) fall through to it.
 */
function scrollToSection(
  slug: string,
  onSelect: (slug: string) => void,
  onSettled: () => void,
  bar: TabBarContext,
) {
  return (e: ReactMouseEvent<HTMLAnchorElement>): void => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const section = document.getElementById(slug);
    if (!section) return;
    e.preventDefault();

    // Own the highlight immediately. Leaving this to the observer is what made
    // a click appear to select the previous tab.
    onSelect(slug);

    // A second tap while the first is still travelling supersedes it, rather
    // than leaving two landings correcting each other.
    bar.pendingScroll.current?.();
    bar.pendingScroll.current = landOnSection(
      slug,
      bar.navRef,
      onSettled,
      bar.tabs,
    );

    // Reflect the section in the URL for deep-linking / back — without adding a
    // history entry per tap or the instant native hash jump.
    history.replaceState(null, "", `#${slug}`);
  };
}

/**
 * The section a URL hash points at, or null if it names none.
 *
 * The hash is whatever a visitor was handed, and `decodeURIComponent` throws
 * URIError on a malformed escape — `#%`, `#%zz`, a truncated `%E0%A4%A`, even a
 * plain `#100%`. Thrown from the deep-link effect below, that reaches the error
 * boundary and blanks the tour page: a public route, broken for anyone who can
 * get a traveler to click a link. Malformed means "no section", not "crash".
 */
export function sectionFromHash(hash: string): string | null {
  const raw = hash.replace(/^#/, "");
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}

/**
 * The element id a click is asking for, or null when the click is not one of
 * ours.
 *
 * Deliberately NOT limited to tab slugs. Anything on this page that a native
 * hash jump would reach lands wrong by the same 85px — the FAQ band's "Jump to"
 * rail points at panels, not tabs, and those links missed the marker exactly
 * like the hero's "(80 reviews)" did. The requirement is only that the target
 * exists; whether it is also a TAB is the caller's business.
 *
 * Every reason to bow out, in one place: a modified click (the reader wants a
 * new tab), a non-primary button, an anchor that is not an in-page one, a bare
 * `#`, or a hash naming nothing on the page. And `defaultPrevented`, which is
 * how the tab bar's own anchors — whose React handler has already run by the
 * time this document-level listener sees the event — avoid being handled twice.
 */
function inPageLinkTarget(event: MouseEvent): string | null {
  if (event.defaultPrevented || event.button !== 0) return null;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
    return null;
  if (!(event.target instanceof Element)) return null;
  const href = event.target.closest("a")?.getAttribute("href");
  if (!href?.startsWith("#")) return null;
  const id = sectionFromHash(href);
  if (!id) return null;
  return document.getElementById(id) ? id : null;
}

/**
 * Land a deep link (`/tours/x/#map`) on the same marker a CLICK lands on.
 *
 * Arriving with a hash is handled by the browser, which uses the document's
 * `scroll-padding-top` (4.5rem / 72px, styles/storefront.css). The scrollspy measures
 * from `markerOffset` — the real header + sticky bar, ~116px. The two
 * disagree by ~44px, so the native jump stops ABOVE the marker, the spy still
 * sees the PREVIOUS section crossing it, and the operator gets "#map took me
 * to the section before, and that one is highlighted".
 *
 * Re-running the click path's own maths is what keeps them from drifting
 * again: same `markerOffset`, same `selectOnClick`, same
 * `forceSectionsLaidOut` (via `tabs`, so it resolves only through the
 * target, same as a click does — a page load has nothing scrolled yet, but
 * that is still no reason to force sections AFTER the one being landed on).
 * `requestAnimationFrame` waits for the native jump and first paint, so
 * this corrects the landing rather than racing it. Mount-only by design —
 * later hash changes come from `scrollToSection`'s `replaceState`, which
 * must not re-trigger a scroll.
 */
function useDeepLinkLanding(
  tabs: readonly TourTab[],
  navRef: RefObject<HTMLElement | null>,
  selectOnClick: (slug: string) => void,
  endClickScroll: () => void,
  pendingScroll: RefObject<(() => void) | null>,
) {
  useEffect(() => {
    const slug = sectionFromHash(window.location.hash);
    if (!slug || !tabs.some((tab) => tab.slug === slug)) return;
    const frame = requestAnimationFrame(() => {
      if (!document.getElementById(slug)) return;
      selectOnClick(slug);
      // Cancel first, same as both click sites in `scrollToSection`'s
      // `TabBarContext` callers: a landing already in flight (a click can
      // beat this rAF to the punch) must release ITS OWN forced sections
      // before this one captures their "previous" style, or the capture
      // reads back "visible" and release re-applies that forever.
      pendingScroll.current?.();
      pendingScroll.current = landOnSection(slug, navRef, endClickScroll, tabs);
    });
    return () => {
      cancelAnimationFrame(frame);
      pendingScroll.current?.();
    };
    // eslint-disable-next-line
  }, []);
}

/**
 * Every OTHER in-page link to a section — the hero's "(80 reviews)", the
 * booking rail's, anything an operator writes into body copy — lands on the
 * same marker as a tab click.
 *
 * Without this they fall to the browser's native jump, which applies the
 * section's `scroll-mt-31` (124px) AND the document's `scroll-padding-top`
 * (72px, styles/storefront.css) and ADDS them: measured landing 196px down against a
 * real marker of 111px. Being 85px short is enough for `pickActiveSection` to
 * still read the PREVIOUS section as current, so clicking "(80 reviews)" in
 * the hero scrolled to the reviews and highlighted "Itinerary".
 *
 * Delegated rather than handed to each link: the anchors live in different
 * components on both sides of the server/client boundary, and the next one
 * somebody adds would otherwise arrive with the bug already in it. A
 * `hashchange` listener would miss the case where the hash is already the one
 * being clicked, which is the second click on the same link.
 *
 * The tab bar's own anchors have already called `preventDefault` by the time
 * this runs (React's listener sits on the root container, inside `document`),
 * so `defaultPrevented` is what keeps them from being handled twice.
 */
function useInPageLinkDelegation(
  tabs: readonly TourTab[],
  navRef: RefObject<HTMLElement | null>,
  selectOnClick: (slug: string) => void,
  endClickScroll: () => void,
  pendingScroll: RefObject<(() => void) | null>,
) {
  useEffect(() => {
    const slugs = new Set(tabs.map((tab) => tab.slug));
    const onDocumentClick = (event: MouseEvent): void => {
      const id = inPageLinkTarget(event);
      if (!id) return;
      event.preventDefault();
      // Only a TAB's section owns the underline. Calling this for a link to
      // something that is not a tab — an FAQ panel, say — would put the bar in
      // a state no tab matches, and lock the observer out of correcting it.
      // Left alone, the observer lights whichever tab's section the landing
      // falls inside, which is the right answer for both cases.
      if (slugs.has(id)) selectOnClick(id);
      pendingScroll.current?.();
      pendingScroll.current = landOnSection(id, navRef, endClickScroll, tabs);
      history.replaceState(null, "", `#${id}`);
    };
    document.addEventListener("click", onDocumentClick);
    return () => document.removeEventListener("click", onDocumentClick);
  }, [tabs, navRef, selectOnClick, endClickScroll, pendingScroll]);
}

/**
 * Publish the marker as a custom property, so anything else that has to pin
 * BELOW this bar can do it in CSS without re-deriving the number.
 *
 * The departures filter is the first such thing (the mobile guide's
 * `.dep-filter` sticks while the departure cards scroll under it). Writing
 * `top-28` there would be a second hardcoded offset of exactly the kind this
 * file's whole design avoids — and it would be wrong on a storefront with an
 * in-flow header, where the bar rests at 0 rather than 64.
 *
 * Re-measured on resize because the bar's height changes with the tab labels
 * wrapping and with the header the tenant renders. `ResizeObserver` on the
 * bar itself, not a window listener: it fires for a font swap too.
 */
function useMarkerPublisher(navRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const bar = navRef.current?.closest<HTMLElement>("[data-tour-tab-bar]");
    if (!bar) return;
    const publish = () => {
      document.documentElement.style.setProperty(
        "--tc-tour-marker",
        `${markerOffset(navRef.current)}px`,
      );
    };
    publish();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(publish);
    observer.observe(bar);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--tc-tour-marker");
    };
  }, [navRef]);
}

/** Keep the active tab in view inside the horizontal mobile rail. */
function useActiveTabIntoView(
  navRef: RefObject<HTMLElement | null>,
  activeSlug: string | null,
) {
  useEffect(() => {
    if (!activeSlug) return;
    const escaped =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(activeSlug)
        : activeSlug;
    const nav = navRef.current;
    const link = nav?.querySelector<HTMLAnchorElement>(
      `a[data-slug="${escaped}"]`,
    );
    if (!nav || !link) return;
    // Center the active tab WITHIN the horizontal rail only. `link.scrollIntoView`
    // also scrolls the *document* vertically to reveal the element — and since
    // `activeSlug` seeds to the first tab, that effect fires on mount and (on
    // iOS Safari especially) yanks the whole page down to this sticky bar,
    // landing the user on the tab strip instead of the hero. Scrolling the
    // nav's own overflow-x never touches the page's vertical scroll.
    const navRect = nav.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    nav.scrollBy({
      left:
        linkRect.left + linkRect.width / 2 - (navRect.left + navRect.width / 2),
    });
  }, [navRef, activeSlug]);
}

/**
 * Sticky storefront section nav (AC-16) — the page's only new client island;
 * every section it points to stays server-rendered.
 *
 * - Pins below the site header via `position: sticky` (no scroll listener).
 * - Navigation = native `href="#slug"` anchors, intercepted on click for a
 *   smooth scroll (see `scrollToSection`); the native jump is the fallback.
 * - Mobile = a horizontal scroll-snap rail (never a dropdown).
 */
export function TourTabsBar({
  tabs,
  shareTitle,
}: {
  tabs: TourTab[];
  /** The tour's name. Present → a Share control rides the right-hand end of
   *  the bar ON PHONES ONLY; the title row keeps the labelled chip from `lg`.
   *  Omitted → no control, which is what the operator preview passes. */
  shareTitle?: string;
}) {
  const t = useTranslator();
  const navRef = useRef<HTMLElement>(null);
  const { activeSlug, selectOnClick, endClickScroll } = useActiveSection(
    tabs,
    navRef,
  );
  /** Cancels the landing currently in flight, if any. */
  const pendingScroll = useRef<(() => void) | null>(null);
  useEffect(() => () => pendingScroll.current?.(), []);

  useDeepLinkLanding(
    tabs,
    navRef,
    selectOnClick,
    endClickScroll,
    pendingScroll,
  );
  useInPageLinkDelegation(
    tabs,
    navRef,
    selectOnClick,
    endClickScroll,
    pendingScroll,
  );
  useMarkerPublisher(navRef);
  useActiveTabIntoView(navRef, activeSlug);

  if (tabs.length === 0) return null;

  const barContext: TabBarContext = { navRef, pendingScroll, tabs };

  return (
    // Guide `.stabs`: an opaque panel with a rule on BOTH edges, not a blurred
    // wash — a translucent bar let the gallery smear through it as the page
    // moved.
    //
    // THE OFFSET DEPENDS ON WHICH HEADER THE STOREFRONT RENDERS, and there are
    // two. The standard `<Nav>` is `fixed`, stays on screen, and owes the bar
    // its 64px so their rules meet as one seam. An APPLIED header from the
    // Header & Footer Builder renders IN FLOW and scrolls away, so 64px
    // reserves nothing — the bar floats with a band of page content sliding
    // through the gap above it, which is the reported bug.
    //
    // `data-in-flow-header` on `<main>` is the existing signal for exactly this
    // question: `site-chrome.tsx` § `mainSpacing` sets it from the same boolean
    // that decides whether `<main>` reserves `pt-16`, precisely "because CSS has
    // to reach the same conclusion on its own". Server-rendered, so the bar pins
    // correctly on the first paint with no measure-then-correct flash.
    //
    // KNOWN GAP, pre-existing and not introduced here: with the standard nav AND
    // a `top` notice bar the pinned chrome is 108px, not 64 (`mainSpacing`
    // reserves `6.75rem` there), so the bar still pins 44px high on those
    // storefronts. `markerOffset` reads the bar's resolved `top`, so the
    // scrollspy stays correct either way.
    <div data-tour-tab-bar className={styles.bar}>
      {/* The container moved OUT to this row so the Share control can sit
          beside the tabs rather than inside them: anything inside the nav is
          inside its horizontal scroller and slides away with the last tab. */}
      <div className={cx(SITE_CONTAINER, styles.row)}>
        <nav
          ref={navRef}
          aria-label={t("Tour Sections")}
          // The first tab's padding is pulled back (in the module) so its
          // LABEL — not its hit area — lines up with the container edge, and
          // therefore with the headline above it.
          className={styles.nav}
        >
          {tabs.map((tab) => {
            const active = tab.slug === activeSlug;
            return (
              <a
                key={tab.slug}
                href={`#${tab.slug}`}
                onClick={scrollToSection(
                  tab.slug,
                  selectOnClick,
                  endClickScroll,
                  barContext,
                )}
                data-slug={tab.slug}
                aria-current={active ? "true" : undefined}
                className={styles.tab}
              >
                {tab.label}
              </a>
            );
          })}
        </nav>
        {shareTitle ? (
          <ShareButton
            title={shareTitle}
            variant="icon"
            className={styles.share}
          />
        ) : null}
      </div>
    </div>
  );
}
